import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AppState,
  InviteAdmin,
  PaginatedRequest,
  RemoveUserRequest,
  TenantHierarchyUsersRequest,
  TenantUser,
  TransferOwnership,
} from '@ra-state';
import { skipWhile } from 'rxjs';
import {
  selectFilteredTenantUsers,
  selectRefreshTenantGrid,
  selectTenantEffectiveRoles,
  selectTenantId,
  selectTenantInfo,
  selectTenantUsers,
  selectTenantUtilityTokenEntitlements,
} from './tenant.selectors';

import {
  archiveTenant,
  createAdminInvitation,
  getTenantDetails,
  getTenantEffectiveRoles,
  getTenantHierarchyUsers,
  getTenantUsers,
  getTenantUtilityTokens,
  removeUserFromTenant,
  resetTenantEffectiveRoles,
  setTenantId,
  setTenantUsers,
  transferOwnership,
  unarchiveTenant,
} from './tenant.actions';

@Injectable({
  providedIn: 'root',
})
export class TenantFacade {
  private readonly store$ = inject<Store<AppState>>(Store);

  tokenEntitlements$ = this.store$
    .select(selectTenantUtilityTokenEntitlements)
    .pipe(skipWhile((tokenEntitlements) => tokenEntitlements === undefined));
  tenantId$ = this.store$.select(selectTenantId);
  tenantUsers$ = this.store$.select(selectTenantUsers);
  filteredTenantUsers$ = this.store$.select(selectFilteredTenantUsers);
  tenantDetails$ = this.store$.select(selectTenantInfo);
  refreshTenantGrid$ = this.store$.select(selectRefreshTenantGrid);
  tenantEffectiveRoles$ = this.store$.select(selectTenantEffectiveRoles);

  getTenantUtilityTokens(tenantId: string): void {
    this.store$.dispatch(getTenantUtilityTokens({ value: tenantId }));
  }

  setTenantId(tenantId: string): void {
    this.store$.dispatch(setTenantId({ tenantId }));
  }

  getTenantUsers(payload: PaginatedRequest): void {
    this.store$.dispatch(getTenantUsers({ payload }));
  }

  setTenantUsers(tenantUsers: TenantUser[]): void {
    this.store$.dispatch(setTenantUsers({ tenantUsers }));
  }

  getTenantHierarchyUsers(payload: TenantHierarchyUsersRequest): void {
    this.store$.dispatch(getTenantHierarchyUsers({ payload }));
  }

  getTenantDetails(tenantId: string): void {
    this.store$.dispatch(getTenantDetails({ value: tenantId }));
  }

  transferOwnership(payload: TransferOwnership): void {
    this.store$.dispatch(transferOwnership({ payload: payload }));
  }

  inviteUserAsAdmin(invite: InviteAdmin): void {
    this.store$.dispatch(createAdminInvitation({ payload: invite }));
  }

  removeUserFromTenant(payload: RemoveUserRequest): void {
    this.store$.dispatch(removeUserFromTenant({ payload }));
  }

  archiveTenant(tenantId: string, archive: boolean): void {
    this.store$.dispatch(archiveTenant({ tenantId, archive }));
  }

  unarchiveTenant(tenantId: string, orgName: string, archive: boolean): void {
    this.store$.dispatch(unarchiveTenant({ tenantId, orgName, archive }));
  }

  getTenantEffectiveRoles(tenantId: string, userId: string): void {
    this.store$.dispatch(getTenantEffectiveRoles({ tenantId, userId }));
  }

  resetTenantEffectiveRoles(): void {
    this.store$.dispatch(resetTenantEffectiveRoles());
  }
}
